import { useContext, useEffect, useRef } from "react";
import { DatabaseSearchContext, Wrapper } from "nystem-components";
import { PhotoTimelineContext } from "../../client/photoTimelineContext.js";
import { Map, View, Feature } from "ol/index.js";
import { Point, LineString } from "ol/geom.js";
import { Tile, Vector as VectorLayer } from "ol/layer.js";
import { OSM, Vector as VectorSource } from "ol/source.js";
import { Style, Stroke, Fill, Circle } from "ol/style.js";
import { useGeographic } from "ol/proj.js";
import { defaults, DragPan } from "ol/interaction.js";
import "ol/ol.css";

// Match the existing map module: photo coordinates remain longitude/latitude.
// eslint-disable-next-line react-hooks/rules-of-hooks
useGeographic();

const PhotographRoute = ({ model }) => {
  const { search } = useContext(DatabaseSearchContext);
  const mapRef = useRef();
  const { selectImage, activeImage } = useContext(PhotoTimelineContext);
  const sourceRef = useRef();
  const activePositionRef = useRef(-1);
  const selectImageRef = useRef(selectImage);
  useEffect(() => { selectImageRef.current = selectImage; }, [selectImage]);
  const images = search?.data;
  const located = (images || []).filter((image) => image.location?.coordinate);

  const activePosition = (images || []).findIndex((image) => image._id === activeImage?._id);
  activePositionRef.current = activePosition;
  useEffect(() => { sourceRef.current?.changed(); }, [activePosition]);
  const coordinates = JSON.stringify(located.map((image) => [image._id, ...image.location.coordinate]));

  useEffect(() => {
    if (!located.length) return undefined;
    const features = located.map((image) => new Feature({ geometry: new Point(image.location.coordinate), image, index: images.indexOf(image) }));
    const source = new VectorSource({ features });
    sourceRef.current = source;
    for (let index = 1; !model.pointsOnly && index < features.length; index++) source.addFeature(new Feature({
      geometry: new LineString([features[index - 1].getGeometry().getCoordinates(), features[index].getGeometry().getCoordinates()]),
      index: features[index].get("index"),
    }));
    const pointStyles = ["#22a447", "#527f96", "#b84b2e"].map((color) => new Style({
      image: new Circle({ radius: 6, fill: new Fill({ color }), stroke: new Stroke({ color: "#fff", width: 1 }) }),
      zIndex: color === "#b84b2e" ? 2 : 1,
    }));
    const lineStyles = ["#22a447", "#527f96"].map((color) => new Style({ stroke: new Stroke({ color, width: 2 }) }));
    let activeFeature;
    let styledPosition;
    const map = new Map({
      target: mapRef.current,
      interactions: defaults({ dragPan: false }).extend([new DragPan({
        condition(event) { return event.originalEvent.pointerType !== "touch" || this.getPointerCount() > 1; },
      })]),
      layers: [new Tile({ source: new OSM() }), new VectorLayer({ source, style: (feature) => {
        const index = feature.get("index");
        if (!feature.get("image")) return lineStyles[index <= activePositionRef.current ? 0 : 1];
        if (styledPosition !== activePositionRef.current) {
          styledPosition = activePositionRef.current;
          activeFeature = features.findLast((feature) => feature.get("index") <= styledPosition);
        }
        return pointStyles[feature === activeFeature ? 2 : index < activePositionRef.current ? 0 : 1];
      } })],
      view: new View({ projection: "EPSG:3857" }),
    });
    map.getView().fit(source.getExtent(), { padding: [40, 40, 40, 40], maxZoom: 16 });
    map.on("singleclick", (event) => {
      const feature = map.forEachFeatureAtPixel(event.pixel, (feature) => feature.get("image") && feature, { hitTolerance: 16 });
      if (!feature) return;
      selectImageRef.current(feature.get("image"));
    });
    return () => { sourceRef.current = undefined; map.setTarget(undefined); map.dispose(); };
    // Region metadata updates should not rebuild an unchanged route.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinates, model.pointsOnly]);

  return <Wrapper>
    {located.length ? <div ref={mapRef} aria-label="Trip map" className="h-[clamp(160px,30svh,360px)] sm:h-[clamp(180px,40vh,600px)] -mx-6 w-[calc(100%+3rem)] overflow-hidden border border-stone-300 lg:h-[min(50vh,640px)]" /> : <p className="border border-stone-300 p-8">No photos with GPS match this selection.</p>}
  </Wrapper>;
};

export default PhotographRoute;
